import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

import { createTheming } from "@callstack/react-theme-provider";

import {
  DEFAULT_THEME,
  GALAXY_THEME_MAP,
  GALAXY_THEME_STORAGE_KEY,
  GalaxyTheme,
} from "./constants";
import type { Theme } from "./types";

const theming = createTheming(DEFAULT_THEME);
// eslint-disable-next-line @typescript-eslint/unbound-method
const { ThemeProvider, withTheme, useTheme } = theming;

// Theme detector interface - allows injection of platform-specific detection
export interface ThemeDetector {
  detectInitialTheme(): Promise<GalaxyTheme.DARK | GalaxyTheme.LIGHT>;
  onThemeChange(callback: (theme: GalaxyTheme.DARK | GalaxyTheme.LIGHT) => void): () => void;
}

// Browser theme detector using media queries
export const BrowserThemeDetector: ThemeDetector = {
  detectInitialTheme: () => {
    // Check browser's preferred color scheme, default to DARK if not available
    if (typeof window !== "undefined" && window.matchMedia) {
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      return Promise.resolve(isDark ? GalaxyTheme.DARK : GalaxyTheme.LIGHT);
    }
    return Promise.resolve(GalaxyTheme.DARK); // Default to dark
  },
  onThemeChange: (callback) => {
    if (typeof window === "undefined" || !window.matchMedia) {
      // No-op cleanup function if not in browser
      return () => {};
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => {
      callback(e.matches ? GalaxyTheme.DARK : GalaxyTheme.LIGHT);
    };

    // Modern browsers use addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }

    // No event listener support
    return () => {};
  },
};

interface GalaxyThemeContextValue {
  // The actual Theme object containing colors, fonts, etc. for styling
  // Used by styled components via withTheme HOC: ${({ theme }) => theme.color.background.primary}
  theme: Theme;

  // The resolved theme enum value (DARK, LIGHT, etc.) - never SYSTEM
  // This is what's actually being rendered after SYSTEM is resolved to the OS theme
  galaxyTheme: Exclude<GalaxyTheme, GalaxyTheme.SYSTEM>;

  // The user's selected theme preference - can be SYSTEM or any specific theme
  // Used by theme selectors to show what the user has chosen
  selectedTheme: GalaxyTheme;

  // The detected OS theme (only DARK or LIGHT) from system preferences
  // Used to show what "System" resolves to in UI, e.g., "System (Dark)"
  systemTheme: GalaxyTheme.DARK | GalaxyTheme.LIGHT;

  // Whether system theme detection is available (via GetSystemTheme or media query)
  isSystemThemeSupported: boolean;

  // Function to update the user's theme preference
  setTheme: (theme: GalaxyTheme) => void;
}

const GalaxyThemeContext = createContext<GalaxyThemeContextValue | undefined>(undefined);

interface GalaxyThemeProviderProps {
  initialTheme?: GalaxyTheme;
  storageKey?: string;
  themeDetector?: ThemeDetector;
  storage?: {
    get: (key: string) => GalaxyTheme | null;
    set: (key: string, theme: GalaxyTheme) => void;
  };
}

const GalaxyThemeProvider = ({
  children,
  initialTheme,
  storageKey = GALAXY_THEME_STORAGE_KEY,
  themeDetector = BrowserThemeDetector,
  storage,
}: PropsWithChildren<GalaxyThemeProviderProps>) => {
  // System theme detection state
  const [systemTheme, setSystemTheme] = useState<GalaxyTheme.DARK | GalaxyTheme.LIGHT>(() => {
    // Default to dark theme
    return GalaxyTheme.DARK;
  });

  const [isSystemThemeSupported] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    // Desktop app or browser - both support system theme
    return true;
  });

  // Track the user's selected theme (can be SYSTEM or a specific theme)
  const [selectedTheme, setSelectedTheme] = useState<GalaxyTheme>(() => {
    // Try storage first
    if (storage) {
      const stored = storage.get(storageKey);
      // Only use stored value if it's a valid theme
      if (stored && Object.values(GalaxyTheme).includes(stored)) {
        return stored;
      }
    }

    // Then initial theme
    if (initialTheme) return initialTheme;

    // Default to SYSTEM theme
    return GalaxyTheme.SYSTEM;
  });

  // System theme detection effect
  useEffect(() => {
    if (typeof window === "undefined" || !themeDetector) {
      return;
    }

    let mounted = true;
    let cleanup: (() => void) | undefined;

    // Initialize theme detection
    const initializeThemeDetection = async () => {
      try {
        // Get initial theme from detector
        const initialTheme = await themeDetector.detectInitialTheme();
        if (!mounted) return;

        setSystemTheme(initialTheme);
      } catch (error) {
        console.error("[GalaxyTheme] Failed to detect initial theme:", error);
        // Default to DARK on error
        setSystemTheme(GalaxyTheme.DARK);
      }

      // Set up theme change listener
      try {
        cleanup = themeDetector.onThemeChange((newTheme) => {
          if (mounted) {
            setSystemTheme(newTheme);
          }
        });
      } catch (error) {
        console.error("[GalaxyTheme] Failed to set up theme change listener:", error);
      }
    };

    // Start initialization
    void initializeThemeDetection();

    return () => {
      mounted = false;
      if (cleanup) {
        cleanup();
      }
    };
  }, [themeDetector]); // Re-run if detector changes

  // Resolve the actual theme to use (resolves SYSTEM to LIGHT/DARK)
  const resolvedTheme: Exclude<GalaxyTheme, GalaxyTheme.SYSTEM> =
    selectedTheme === GalaxyTheme.SYSTEM ? systemTheme : selectedTheme;

  const setTheme = useCallback(
    (theme: GalaxyTheme) => {
      setSelectedTheme(theme);
      if (storage) {
        storage.set(storageKey, theme);
      }
    },
    [storage, storageKey],
  );

  // Get the actual theme object, handling SYSTEM case
  const actualTheme = GALAXY_THEME_MAP[resolvedTheme] || GALAXY_THEME_MAP[GalaxyTheme.DARK];

  const value: GalaxyThemeContextValue = useMemo(
    () => ({
      theme: actualTheme,
      galaxyTheme: resolvedTheme, // Components get the resolved theme, not SYSTEM
      selectedTheme, // The user's actual selection (can be SYSTEM)
      systemTheme, // The detected system theme
      isSystemThemeSupported, // Whether system theme detection is available
      setTheme,
    }),
    [actualTheme, resolvedTheme, selectedTheme, systemTheme, isSystemThemeSupported, setTheme],
  );

  return (
    <GalaxyThemeContext.Provider value={value}>
      <ThemeProvider theme={value.theme}>{children}</ThemeProvider>
    </GalaxyThemeContext.Provider>
  );
};

export const useGalaxyTheme = () => {
  const context = useContext(GalaxyThemeContext);
  if (context === undefined) {
    throw new Error("useGalaxyTheme must be used within a GalaxyThemeProvider");
  }
  return context;
};

export { GalaxyThemeProvider, ThemeProvider, useTheme, withTheme };
